// Apartments.js
import React, { useEffect, useState } from "react";
import Pagination from "../common/Pagination";
import { Link } from "react-router-dom";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import OwnerMenu from "../owner/OwnerMenu";
import CreateApartmentModal from "../owner/OwnerModals/CreateApartmentModal";
import { useTranslation } from "react-i18next";
import BookingCalendar from "../booking/BookingCalendar";

const Apartments = () => {
  const { t } = useTranslation();
  const [apartments, setApartments] = useState([]);
  const [locations, setLocations] = useState({});
  const [locationList, setLocationList] = useState([]);
  const [location, setLocation] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [userGroup, setUserGroup] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apartmentName, setApartmentName] = useState("");
  const [rooms, setRooms] = useState("");
  const [size, setSize] = useState("");
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [apartmentIdToUpdate, setApartmentIdToUpdate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState(null);
  const [bookingComId, setBookingComId] = useState("");
  const [airbnbId, setAirbnbId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;
  const [reviewScores, setReviewScores] = useState({});

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  const openCalendar = (apartmentId) => {
    setSelectedApartmentId(apartmentId);
    setIsCalendarOpen(true);
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
    setSelectedApartmentId(null);
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/locations/`)
      .then((response) => response.json())
      .then((data) => setLocationList(data))
      .catch((error) => console.error("Error fetching locations:", error));

    fetch(`${getApiBaseUrl()}/api/facilities/facilities/`)
      .then((response) => response.json())
      .then((data) => setFacilities(data))
      .catch((error) => console.error("Error fetching facilities:", error));
  }, []);

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/reviews/`)
      .then((res) => res.json())
      .then((data) => {
        const grouped = {};
        data.forEach((review) => {
          const aptId = review.apartment;
          if (!grouped[aptId]) grouped[aptId] = [];
          grouped[aptId].push(review.value);
        });
        const scores = {};
        Object.entries(grouped).forEach(([aptId, values]) => {
          const avg = values.reduce((a, b) => a + b, 0) / values.length;
          scores[aptId] = Math.min(5, Math.max(1, Math.ceil(avg)));
        });
        setReviewScores(scores);
      })
      .catch((err) => console.error("Error fetching reviews:", err));
  }, []);

  useEffect(() => {
    const storedUserId = localStorage.getItem("user_id");
    const rawGroups = localStorage.getItem("groups");
    let storedGroups = [];

    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        storedGroups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        storedGroups = [rawGroups];
      }
    }

    setUserId(storedUserId);
    
    if (storedGroups.includes("Owners")) {
      setUserGroup("Owners");
    }

    const endpoint = storedGroups.includes("Owners") && storedUserId
      ? `${getApiBaseUrl()}/api/properties/apartments/?owner=${storedUserId}`
      : `${getApiBaseUrl()}/api/properties/apartments/`;

    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        setApartments(data);
        data.forEach((apartment) => {
          fetch(`${getApiBaseUrl()}/api/locations/${apartment.location}/`)
            .then((response) => response.json())
            .then((locationData) => {
              setLocations((prev) => ({ ...prev, [apartment.location]: locationData.name }));
            });
        });
      })
      .catch((error) => console.error("Error fetching apartments:", error));
  }, []);

  const handleDelete = async (apartmentId) => {
    if (!window.confirm(t("are_you_sure_delete_apartment"))) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/`, {
        method: "DELETE",
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
      });

      if (response.ok) {
        setApartments((prev) => prev.filter((apt) => apt.id !== apartmentId));
        alert(t("apartment_deleted_successfully"));
      } else {
        alert(t("error_deleting_apartment"));
      }
    } catch (error) {
      console.error("Error deleting apartment:", error);
      alert(t("error_occurred_try_again"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const apartmentPayload = {
        apartment_name: apartmentName,
        location: parseInt(location, 10),
        rooms: parseInt(rooms, 10),
        size: parseFloat(size),
        facilities: selectedFacilities,
        owner: parseInt(userId, 10),
        booking_com_id: bookingComId,  // Add this
        airbnb_id: airbnbId
      };

      console.log("Submitting apartment payload:", JSON.stringify(apartmentPayload));
      let response;
      if (isUpdateMode) {
        response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentIdToUpdate}/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Token " + localStorage.getItem("token"),
          },
          body: JSON.stringify(apartmentPayload),
        });
      } else {
        response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Token " + localStorage.getItem("token"),
          },
          body: JSON.stringify(apartmentPayload),
        });
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error("Apartment save error:", errorBody);
        throw new Error(t("failed_create_update_apartment"));
      }

      const data = await response.json();
      console.log("Apartment created/updated:", data);

      if (isUpdateMode && selectedFiles.length > 0) {
        const apartmentId = apartmentIdToUpdate || data.id;
        console.log("Selected Files:", selectedFiles);
        await Promise.all(
          selectedFiles.map(async (file) => {
            const formData = new FormData();
            formData.append("image", file);
            formData.append("size_in_bytes", file.size);
            formData.append("format", file.type.split("/")[1].toUpperCase());
            formData.append("apartment", apartmentId);

            const uploadResponse = await fetch(`${getApiBaseUrl()}/api/pictures/`, {
              method: "POST",
              headers: {
                Authorization: "Token " + localStorage.getItem("token"),
              },
              body: formData,
            });

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              throw new Error(`${t("failed_upload_picture")}: ${errorText}`);
            }
          })
        );
      }

      alert(t("apartment_created_updated_successfully"));
      setIsModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Error creating/updating apartment:", error);
      setError(t("failed_create_update_apartment_try_again"));
    }
  };

  const handleFacilityChange = (facilityId) => {
    setSelectedFacilities((prev) =>
      prev.includes(facilityId)
        ? prev.filter((id) => id !== facilityId)
        : [...prev, facilityId]
    );
  };

  const handleFileChange = (e) => {
    setSelectedFiles([...e.target.files]);
  };

  const openCreateModal = () => {
    setIsUpdateMode(false);
    setApartmentIdToUpdate(null);
    setApartmentName("");
    setLocation("");
    setRooms("");
    setSize("");
    setSelectedFacilities([]);
    setSelectedFiles([]);
    setBookingComId("");  // Add this
    setAirbnbId("");      // Add this
    setIsModalOpen(true);
  };

  const openUpdateModal = (apartment) => {
    setIsUpdateMode(true);
    setApartmentIdToUpdate(apartment.id);
    setApartmentName(apartment.apartment_name);
    setLocation(apartment.location);
    setRooms(apartment.rooms);
    setSize(apartment.size);
    setSelectedFacilities(apartment.facilities);
    setBookingComId(apartment.booking_com_id || "");  // Add this
    setAirbnbId(apartment.airbnb_id || ""); 
    setSelectedFiles([]);
    setIsModalOpen(true);
  };

  const StarRating = ({ stars }) => (
    <div className="flex justify-center text-xl">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= stars ? "text-yellow-400" : "text-gray-300"}>★</span>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      {userGroup === "Owners" && <OwnerMenu />}
      <div className="flex flex-col items-center mt-20 px-4">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">{t("apartments")}</h1>

        {userGroup === "Owners" && apartments.length === 0 && (
          <p className="text-red-600 font-semibold mb-4">
            {t("No apartments available")}
          </p>
        )}

        {userGroup === "Owners" && (
          <button
            onClick={openCreateModal}
            className="bg-blue-500 text-white px-4 py-2 rounded-md mb-4 hover:bg-blue-700"
          >
            {t("Create Apartment")}
          </button>
        )}

        <CreateApartmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          isUpdateMode={isUpdateMode}
          apartmentName={apartmentName}
          setApartmentName={setApartmentName}
          location={location}
          setLocation={setLocation}
          rooms={rooms}
          setRooms={setRooms}
          size={size}
          setSize={setSize}
          selectedFacilities={selectedFacilities}
          handleFacilityChange={handleFacilityChange}
          selectedFiles={selectedFiles}
          handleFileChange={handleFileChange}
          error={error}
          locationList={locationList}
          facilities={facilities}
          // ...existing props...
          bookingComId={bookingComId}
          setBookingComId={setBookingComId}
          airbnbId={airbnbId}
          setAirbnbId={setAirbnbId}
        />

        {/* Desktop table — hidden on mobile */}
        <div className="hidden md:block w-full max-w-4xl overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-center">
                <th className="py-3 px-4 border-b">{t("apartment_name")}</th>
                <th className="py-3 px-4 border-b">{t("location")}</th>
                <th className="py-3 px-4 border-b">{t("Prices")}</th>
                <th className="py-3 px-4 border-b">{t("review")}</th>
                {userGroup === "Owners" && <th className="py-3 px-4 border-b">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {apartments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((apartment) => (
                <tr key={apartment.id} className="hover:bg-gray-50 text-center">
                  <td className="py-3 px-4 border-b">
                    <Link to={`/apartments/${apartment.id}`} className="text-blue-600 hover:text-blue-800">
                      {apartment.apartment_name}
                    </Link>
                  </td>
                  <td className="py-3 px-4 border-b">
                    {locations[apartment.location] || t("loading")}
                  </td>
                  <td className="py-3 px-4 border-b">
                    <button
                      onClick={() => openCalendar(apartment.id)}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      {t("Prices per Night")}
                    </button>
                  </td>
                  <td className="py-3 px-4 border-b">
                    {reviewScores[apartment.id] !== undefined ? (
                      <Link to={`/apartments/${apartment.id}/reviews`}>
                        <StarRating stars={reviewScores[apartment.id]} />
                      </Link>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  {userGroup === "Owners" && (
                    <td className="py-3 px-4 border-b flex justify-center space-x-2">
                      <button
                        onClick={() => openUpdateModal(apartment)}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        {t("update")}
                      </button>
                      <button
                        onClick={() => handleDelete(apartment.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700"
                      >
                        {t("delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards — hidden on desktop */}
        <div className="md:hidden w-full flex flex-col gap-4">
          {apartments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((apartment) => (
            <div key={apartment.id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <Link to={`/apartments/${apartment.id}`} className="text-lg font-semibold text-blue-600 hover:text-blue-800">
                {apartment.apartment_name}
              </Link>
              <p className="text-gray-600 text-sm mt-1">{locations[apartment.location] || t("loading")}</p>
              {reviewScores[apartment.id] !== undefined && (
                <div className="mt-1">
                  <Link to={`/apartments/${apartment.id}/reviews`}>
                    <StarRating stars={reviewScores[apartment.id]} />
                  </Link>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => openCalendar(apartment.id)}
                  className="bg-blue-500 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700"
                >
                  {t("Prices per Night")}
                </button>
                {userGroup === "Owners" && (
                  <>
                    <button
                      onClick={() => openUpdateModal(apartment)}
                      className="bg-green-500 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700"
                    >
                      {t("update")}
                    </button>
                    <button
                      onClick={() => handleDelete(apartment.id)}
                      className="bg-red-500 text-white px-3 py-1.5 rounded text-sm hover:bg-red-700"
                    >
                      {t("delete")}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>        <Pagination totalItems={apartments.length} pageSize={PAGE_SIZE} currentPage={currentPage} onPageChange={setCurrentPage} />      </div>

      {isCalendarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg w-[90%] max-w-md mx-auto">
            <button
              onClick={closeCalendar}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✖
            </button>
              <BookingCalendar 
                apartmentId={selectedApartmentId} 
                onClose={closeCalendar}
              />
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default Apartments;